import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Alert, Badge, Button, ErrorState, Modal, Page, PageHeader, Segmented, Skeleton } from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import type { BillingCycle, TierCode } from '../api/billingApi';
import { BILLING_CYCLE_OPTIONS, FREE_MONTHS_ANNUAL, TIER_COPY, TIER_ORDER, getTrialMonths } from '../constants/tierCopy';
import { calcTrialDaysLeft, resolveCtaAction } from '../lib/ctaResolution';
import { PRICING_GRID_CLASS, PricingCard } from '../components/PricingCard';

// [개발전용] 알림톡 발송 파이프라인(Outbox→RabbitMQ→Hermes) 실동작 확인용 7종 — 구독결제_알림톡_
// 알림유형_정의.md 7개 시나리오와 1:1 대응. 실서비스 배포 전 이 배열·버튼 모두 제거 대상.
const NOTIFICATION_TESTS: { key: string; label: string; run: () => Promise<unknown> }[] = [
  { key: 'trial-ending', label: '무료체험 종료 예고', run: billingApi.testNotificationTrialEnding },
  { key: 'payment-upcoming', label: '정기결제 사전 안내', run: billingApi.testNotificationPaymentUpcoming },
  { key: 'payment-succeeded', label: '정기결제 성공', run: billingApi.testNotificationPaymentSucceeded },
  { key: 'payment-failed', label: '정기결제 실패', run: billingApi.testNotificationPaymentFailed },
  { key: 'payment-method-changed', label: '결제정보 변경(확인)', run: billingApi.testNotificationPaymentMethodChanged },
  { key: 'referral-link-issued', label: '추천인 링크 발급 안내', run: billingApi.testNotificationReferralLinkIssued },
];

function cycleLabel(cycle: BillingCycle) {
  return cycle === 'annual' ? '연간 결제' : '월간 결제';
}

/** BE resolveStaffSurchargeForCycle과 동일 배수 — 직원 추가요금은 월 단가 × 결제주기 개월수로
 *  청구된다(연간이면 12개월치를 한 번에). getStaffUsage가 내려주는 값은 항상 "월" 단가다. */
function cycleMonths(cycle: BillingCycle) {
  return cycle === 'annual' ? 12 : 1;
}

function formatWon(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('ko-KR');
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** BusinessException의 data 필드(구체적 거절 사유, 예: "1회성 결제 구독은 예약할 수 없어요")를
 *  그대로 보여준다 — CheckoutPage.tsx·PaymentMethodPage.tsx와 동일 관례(2026-09-11, 예약 API들이
 *  일괄 실패 문구만 쓰던 것을 여기서도 맞춤). */
function extractErrorReason(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { data?: string; message?: string } } })?.response?.data;
  return data?.data ?? data?.message ?? fallback;
}

const VALID_TIER_CODES: TierCode[] = ['FREE', 'STARTER', 'PRO', 'ALLIN'];

export function TierChangePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // 요금제 비교 페이지의 다운그레이드 클릭이 ?tier=로 목표 티어를 넘겨줌(PricingPlansPage.tsx) — 있으면 미리 선택
  const initialTier = searchParams.get('tier');
  const [selectedTier, setSelectedTier] = useState<TierCode | null>(
    initialTier && VALID_TIER_CODES.includes(initialTier as TierCode) ? (initialTier as TierCode) : null,
  );
  // 결제 주기 변경 예약 진입(월간↔연간 양방향, 2026-09-10 정책 변경 — 예전엔 연간→월간만 예약이고
  // 월간→연간은 즉시결제였으나 이제 방향 무관 항상 예약) — 요금제 비교 화면에서 지금 이용 중인
  // 티어의 카드를 다른 주기로 토글한 채 눌렀을 때만 실려온다(PricingPlansPage.tsx). 값이 있으면
  // 이 화면은 티어 선택 대신 결제 주기 예약 확인만 보여준다.
  const reserveCycleParam = searchParams.get('reserveCycle') as BillingCycle | null;

  // 요금제 비교 화면에서 다른 티어 + 다른 결제주기 토글을 동시에 고른 채 업그레이드/다운그레이드를
  // 눌렀을 때 ?cycle=로 그 주기 선택을 그대로 넘겨받아 미리 선택해둔다(PricingPlansPage.tsx,
  // 2026-09-10 — 예전엔 이 토글 선택이 조용히 무시됐음). reserveCycleParam(?reserveCycle=, 순수
  // 주기 변경 전용 진입)과는 별개 — 이 화면 안에서 직접 주기를 바꾸는 버튼도 같은 상태를 공유한다.
  const initialCycle = searchParams.get('cycle');
  const [billingCycleOverride, setBillingCycleOverride] = useState<BillingCycle | null>(
    initialCycle === 'monthly' || initialCycle === 'annual' ? initialCycle : null,
  );

  // 결제 주기 예약 확인 화면(reserveCycleParam)은 티어 그리드가 숨겨져 있어(아래 참고) 이 응답을
  // 쓸 곳이 없다 — 매번 불필요하게 요금제 카탈로그 전체를 불러오지 않도록 그때만 끈다.
  const plansQuery = useQuery({
    queryKey: ['billing', 'pricingPlans'],
    queryFn: async ({ signal }) => (await billingApi.getPricingPlans(signal)).data.data,
    enabled: !reserveCycleParam,
  });
  const subscriptionQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async ({ signal }) => (await billingApi.getSubscription(signal)).data.data,
  });
  const siteProgressQuery = useQuery({
    queryKey: ['billing', 'siteProgressCount'],
    queryFn: async ({ signal }) => (await billingApi.getSiteProgressCount(signal)).data.data,
  });
  // 결제 주기 예약(CYCLE_CHANGE·TIER_AND_CYCLE_CHANGE)의 "다음 결제 예정 금액"에 직원 추가요금을
  // 같이 보여주기 위해 조회(StaffFeePage와 동일 API 재사용). surchargeAmount는 항상 "월" 단가라
  // 연간 전환이면 cycleMonths로 배수를 곱해야 한다.
  const staffUsageQuery = useQuery({
    queryKey: ['billing', 'staffUsage'],
    queryFn: async ({ signal }) => (await billingApi.getStaffUsage(signal)).data.data,
  });
  const sub = subscriptionQuery.data;

  // 이미 걸려있는 예약(다운그레이드·주기변경·동시변경)이 있는지 — BE가 2026-09-18부터 새 예약/즉시
  // 업그레이드 요청이 들어오면 기존 예약을 알아서 덮어쓰거나 지우도록 고쳤지만(SubscriptionService
  // 참고), 사용자 입장에선 "내가 걸어둔 예약이 말없이 바뀐다"는 게 놀랄 만한 일이라 실행 직전에
  // 확인을 한 번 받는다. 실제로 덮어쓰는 주체는 BE라 여기서는 안내·확인만 하고, 예약 취소 API를
  // 별도로 먼저 호출하지 않는다 — 확인만 받으면 원래 하려던 요청을 그대로 보낸다.
  const hasPendingReservation = Boolean(sub?.nextTierCode || sub?.nextBillingCycle);
  type PendingConfirmAction =
    | { type: 'downgrade'; targetTierCode: TierCode }
    | { type: 'cycle'; targetBillingCycle: BillingCycle }
    | { type: 'combined'; targetTierCode: TierCode; targetBillingCycle: BillingCycle }
    | { type: 'upgrade'; targetTierCode: TierCode; diff: number };
  const [pendingConfirmAction, setPendingConfirmAction] = useState<PendingConfirmAction | null>(null);

  function runTierChangeAction(action: PendingConfirmAction) {
    if (action.type === 'downgrade') reserveDowngradeMutation.mutate(action.targetTierCode);
    else if (action.type === 'cycle') reserveCycleChangeMutation.mutate(action.targetBillingCycle);
    else if (action.type === 'combined') {
      reserveCombinedChangeMutation.mutate({ targetTierCode: action.targetTierCode, targetBillingCycle: action.targetBillingCycle });
    } else {
      navigate(`/agents/billing/checkout?tier=${action.targetTierCode}&diff=${action.diff}`);
    }
  }

  // 이미 예약이 있으면 실행 전에 한 번 확인받고, 없으면 곧바로 실행한다.
  function confirmThenRun(action: PendingConfirmAction) {
    if (hasPendingReservation) {
      setPendingConfirmAction(action);
    } else {
      runTierChangeAction(action);
    }
  }

  // 카드 자체는 요금제 비교 화면(PricingPlansPage)과 완전히 동일한 컴포넌트(PricingCard)·판정
  // 로직(resolveCtaAction/resolveCtaLabel)을 쓴다(2026-09-17, 사용자 요청 — 카드 통합). 클릭 후
  // 흐름만 화면별로 다르다: 여기서는 trial만 즉시 라우팅하고, upgrade/downgrade는 예전처럼 카드를
  // "선택"만 해서 아래 요약 패널에서 확인 후 확정한다. sub의 실제 결제주기를 그대로 넘겨 cycle-change
  // 판정이 절대 나오지 않게 한다 — 이 화면의 주기 변경은 위 Segmented 토글이 별도로 전담한다.
  const hasEverPaid = sub?.hasEverPaid ?? false;
  const isTrialing = sub?.subStatus === 'TRIAL';
  const trialMonths = getTrialMonths(false);

  // 카드를 고르면 아래 변경 요약이 화면 밖일 수 있어 그쪽으로 스크롤한다.
  const summaryRef = useRef<HTMLDivElement>(null);

  function handleGridCtaClick(tierCode: TierCode) {
    const action = resolveCtaAction(tierCode, true, sub, sub?.billingCycle ?? 'monthly');
    if (action === 'current') return; // 버튼이 이미 disabled — 도달하지 않음
    if (action === 'trial') {
      navigate(`/agents/billing/checkout?tier=${tierCode}&mode=trial`);
      return;
    }
    setSelectedTier(tierCode);
    requestAnimationFrame(() => summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }

  // ACTIVE 상태의 유료 티어만 결제 주기 변경 허용(BE의 isCycleReserveEligible과 동일 기준) —
  // TRIAL·FREE·CANCELED·PAST_DUE·GRACE에서 주기만 바꾸면 해지 예약·연체 상태를 의도치 않게
  // 되돌리는 부작용이 있다.
  const canChangeCycle = Boolean(
    !reserveCycleParam && sub && sub.tierCode !== 'FREE' && sub.subStatus === 'ACTIVE' && sub.billingCycle != null,
  );
  const cycleChanged = canChangeCycle && billingCycleOverride != null && billingCycleOverride !== sub!.billingCycle;
  const tierChanged = !reserveCycleParam && selectedTier != null && sub != null && selectedTier !== sub.tierCode;
  // 티어와 주기가 동시에 바뀌면 방향(업/다운그레이드 · 월간↔연간) 무관하게 항상 예약이다(BE
  // reserveCombinedChange, 2026-09-10 정책 변경 — 예전엔 "티어+연간전환"만 즉시결제였음).
  const bothChanged = tierChanged && cycleChanged;

  const previewTargetTier: TierCode | null = reserveCycleParam
    ? selectedTier
    : tierChanged
      ? selectedTier
      : cycleChanged
        ? (sub?.tierCode ?? null)
        : null;
  const previewTargetCycle: BillingCycle | undefined = reserveCycleParam
    ? reserveCycleParam
    : cycleChanged
      ? (billingCycleOverride ?? undefined)
      : undefined;

  const previewQuery = useQuery({
    queryKey: ['billing', 'tierChangePreview', previewTargetTier, previewTargetCycle],
    queryFn: async ({ signal }) =>
      (await billingApi.getTierChangePreview(previewTargetTier as TierCode, previewTargetCycle, signal)).data.data,
    enabled: previewTargetTier != null,
  });

  const reserveDowngradeMutation = useMutation({
    mutationFn: (targetTierCode: TierCode) => billingApi.reserveDowngrade(targetTierCode),
    onSuccess: () => {
      toast.success('다운그레이드가 예약됐어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      navigate('/agents/billing/manage');
    },
    onError: (error: unknown) => toast.error(extractErrorReason(error, '예약에 실패했습니다. 잠시 후 다시 시도해주세요.')),
  });

  const reserveCycleChangeMutation = useMutation({
    mutationFn: (targetBillingCycle: BillingCycle) => billingApi.reserveCycleChange(targetBillingCycle),
    onSuccess: (_data, targetBillingCycle) => {
      toast.success(`${cycleLabel(targetBillingCycle)}로 전환 예약됐어요`);
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      navigate('/agents/billing/manage');
    },
    onError: (error: unknown) => toast.error(extractErrorReason(error, '예약에 실패했습니다. 잠시 후 다시 시도해주세요.')),
  });

  // 티어+주기 동시 변경 예약(2026-09-10 신설 API, PATCH /subscription/combined-change) — 방향
  // 무관 항상 예약이라 한 번의 호출로 끝난다. 예전엔 다운그레이드+월간전환 조합만 두 API를 순차
  // 호출한 뒤 실패 시 롤백하는 방식이었는데, BE가 원자적으로 처리하는 전용 API를 새로 내려줘서
  // 그 복잡도가 전부 사라졌다.
  const reserveCombinedChangeMutation = useMutation({
    mutationFn: ({ targetTierCode, targetBillingCycle }: { targetTierCode: TierCode; targetBillingCycle: BillingCycle }) =>
      billingApi.reserveCombinedChange(targetTierCode, targetBillingCycle),
    onSuccess: () => {
      toast.success('요금제 변경과 결제 주기 전환이 함께 예약됐어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      navigate('/agents/billing/manage');
    },
    onError: (error: unknown) => toast.error(extractErrorReason(error, '예약에 실패했습니다. 잠시 후 다시 시도해주세요.')),
  });

  // [개발전용] 테스트 데이터를 빠르게 FREE로 되돌리기 위한 임시 버튼 — 실서비스 배포 전 제거 대상
  const debugResetToFreeMutation = useMutation({
    mutationFn: () => billingApi.debugResetToFree(),
    onSuccess: () => {
      toast.success('구독이 FREE로 초기화됐어요');
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      // 결제 주기 예약 확인 진입(reserveCycleParam)이면 티어 그리드가 숨겨져 있어(위 참고)
      // selectedTier만 비우면 더 이상 아무것도 고를 수 없는 막다른 화면이 된다 — 요금제 비교
      // 화면으로 보낸다. 일반 모드는 기존처럼 그리드에서 다시 고르도록 selectedTier만 비운다.
      if (reserveCycleParam) {
        navigate('/agents/billing');
      } else {
        setSelectedTier(null);
      }
    },
    onError: () => toast.error('초기화에 실패했습니다.'),
  });

  // [개발전용] 알림톡 발송 파이프라인 실동작 확인용 — 실서비스 배포 전 제거 대상
  const [pendingNotificationTestKey, setPendingNotificationTestKey] = useState<string | null>(null);
  const notificationTestMutation = useMutation({
    mutationFn: (test: (typeof NOTIFICATION_TESTS)[number]) => test.run(),
    onMutate: (test) => setPendingNotificationTestKey(test.key),
    onSuccess: (_data, test) => toast.success(`[${test.label}] 알림톡 발송 요청을 보냈어요`),
    onError: (_error, test) => toast.error(`[${test.label}] 발송 요청에 실패했습니다.`),
    onSettled: () => setPendingNotificationTestKey(null),
  });

  const plans = plansQuery.data;
  const preview = previewTargetTier ? previewQuery.data : undefined;
  const isPreviewRequested = previewTargetTier != null;
  const cycleReserveTarget: BillingCycle | null = reserveCycleParam ?? (cycleChanged ? billingCycleOverride : null);
  // 예약이 반영되는 다음 결제일에 실제로 청구될 금액 — preview.targetMonthlyPrice는 이름과 달리
  // 목표 주기가 연간이면 연간 정가가 그대로 들어있다(BE calcTierChange의 targetBasisPrice 참고).
  // 직원 추가요금은 예약 시점엔 0으로 내려오므로(BE, 실제 청구 시점 인원으로 다시 계산) 여기선
  // getStaffUsage의 현재 월 단가 × 결제주기 개월수로 참고용 추정치만 보여준다 — 다음 결제일까지
  // 인원이 바뀌면 실제 청구액은 달라질 수 있다(안내 문구로 명시).
  const staffMonthlySurcharge = staffUsageQuery.data?.surchargeAmount ?? 0;
  const cycleReserveStaffSurcharge = cycleReserveTarget ? staffMonthlySurcharge * cycleMonths(cycleReserveTarget) : 0;
  const combinedReserveStaffSurcharge = billingCycleOverride
    ? staffMonthlySurcharge * cycleMonths(billingCycleOverride)
    : 0;
  const showPlaceholder = !selectedTier && !cycleChanged;

  const activeSiteCount = siteProgressQuery.data?.totalElements;
  // 현장상한 차단은 방향과 무관하게 "목표 티어의 상한"이 기준 — TIER_AND_CYCLE_CHANGE(티어+주기
  // 동시 변경)로 더 낮은 티어를 고른 경우도 순수 DOWNGRADE와 동일하게 막아야 한다(그렇지 않으면
  // 현장 정리 없이 예약해버릴 수 있다).
  const isBlocked =
    (preview?.direction === 'DOWNGRADE' || preview?.direction === 'TIER_AND_CYCLE_CHANGE') &&
    preview.targetMaxConcurrentSite != null &&
    activeSiteCount != null &&
    activeSiteCount > preview.targetMaxConcurrentSite;

  return (
    <Page width="xl">
      <PageHeader
        title="요금제 변경"
        subtitle={
          reserveCycleParam
            ? `지금 바로 청구되는 금액 없이, 다음 결제일부터 ${cycleLabel(reserveCycleParam)}로 전환돼요`
            : '업그레이드는 바로, 다운그레이드는 다음 결제일부터 적용돼요'
        }
        onBack={() => navigate('/agents/billing/manage')}
      />

      {/* 현재 상태 스트립 */}
      <div className="mt-06 app-card">
        <div className="body">
          {subscriptionQuery.isLoading ? (
            <Skeleton height={24} />
          ) : (
            <p className="text-body-sm text-text">
              <span className="mr-02 inline-block h-02 w-02 rounded-full bg-group align-middle" aria-hidden="true" />
              현재 <strong className="text-text">{sub ? TIER_COPY[sub.tierCode].displayName : '—'}</strong> 요금제를
              이용 중이에요
              {sub?.tierCode !== 'FREE' && <> · 다음 결제일 {formatDate(sub?.nextBillingDtm)}</>} · 진행 중 현장{' '}
              {activeSiteCount ?? '—'}개 사용 중
            </p>
          )}
        </div>
      </div>

      {/* 결제 주기 변경 버튼 — 티어 선택과 별개로 이 화면에서 바로 주기를 바꿀 수 있다(사용자 요청,
          2026-09-09). 방향(월간↔연간) 무관하게 항상 예약이라(2026-09-10 정책 변경), 다른 티어를
          같이 고른 채 주기를 바꾸면 BE가 티어+주기를 한 번에 예약하는 전용 API로 처리한다. */}
      {canChangeCycle && (
        <div className="mt-06 flex justify-center">
          <Segmented
            options={BILLING_CYCLE_OPTIONS}
            value={billingCycleOverride ?? sub!.billingCycle!}
            onChange={(v) => {
              const next = v as BillingCycle;
              setBillingCycleOverride(next === sub!.billingCycle ? null : next);
            }}
          />
        </div>
      )}

      {/* [개발전용] 테스트 데이터를 빠르게 FREE로 되돌리는 임시 버튼 — 실서비스 배포 전 제거 대상 */}
      <Alert kind="warning" className="mt-04" title="개발전용 · 테스트 데이터 리셋">
        지금 로그인한 계정의 구독을 즉시 FREE로 되돌려요. 실서비스에는 노출되지 않아야 해요.
        <div className="mt-03">
          <Button
            variant="danger"
            size="sm"
            loading={debugResetToFreeMutation.isPending}
            disabled={debugResetToFreeMutation.isPending}
            onClick={() => debugResetToFreeMutation.mutate()}
          >
            지금 FREE로 초기화
          </Button>
        </div>
      </Alert>

      {/* [개발전용] 알림톡 발송 파이프라인(Outbox→RabbitMQ→Hermes) 실동작 확인용 — 실서비스 배포 전 제거 대상 */}
      <Alert kind="warning" className="mt-04" title="개발전용 · 알림톡 발송 테스트">
        배치 조건(D-day 도달, 실제 결제 성공/실패 등)을 기다리지 않고 표본 데이터로 지금 바로 한 번씩
        발송해요. 같은 알림을 24시간 안에 반복 테스트하면 hermes가 중복으로 보고 실제 채널 발송은
        건너뛸 수 있어요(billing 쪽 Outbox 적재·발행 자체는 매번 정상 동작).
        <div className="mt-03 flex flex-wrap gap-02">
          {NOTIFICATION_TESTS.map((test) => (
            <Button
              key={test.key}
              variant="secondary"
              size="sm"
              loading={pendingNotificationTestKey === test.key}
              disabled={notificationTestMutation.isPending}
              onClick={() => notificationTestMutation.mutate(test)}
            >
              {test.label}
            </Button>
          ))}
        </div>
      </Alert>

      {/* 티어 선택 카드 Grid — 결제 주기 예약 진입(reserveCycleParam)이면 티어는 그대로 두고 주기만
          바꾸는 흐름이라 숨긴다. */}
      {!reserveCycleParam && (
        plansQuery.isLoading || subscriptionQuery.isLoading ? (
          <div className={`mt-06 ${PRICING_GRID_CLASS}`}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={420} />
            ))}
          </div>
        ) : plansQuery.isError || !plans || !sub ? (
          <ErrorState className="mt-06" title="요금제 정보를 불러오지 못했습니다" />
        ) : (
          <div className={`mt-06 ${PRICING_GRID_CLASS}`}>
            {TIER_ORDER.map((tierCode) => {
              const tier = plans.tiers.find((t) => t.tierCode === tierCode);
              if (!tier) return null;
              const action = resolveCtaAction(tierCode, true, sub, sub.billingCycle ?? 'monthly');
              const trialDaysLeft = action === 'current' ? calcTrialDaysLeft(sub.trialEndDtm) : null;
              return (
                <PricingCard
                  key={tierCode}
                  tier={tier}
                  copy={TIER_COPY[tierCode]}
                  billingCycle={sub.billingCycle ?? 'monthly'}
                  freeMonthsAnnual={FREE_MONTHS_ANNUAL}
                  state={action}
                  trialDaysLeft={trialDaysLeft}
                  hasEverPaid={hasEverPaid}
                  isTrialing={isTrialing}
                  trialMonths={trialMonths}
                  selected={tierCode === selectedTier && tierCode !== sub.tierCode}
                  onCtaClick={handleGridCtaClick}
                />
              );
            })}
          </div>
        )
      )}

      {/* 변경 요약 */}
      <div ref={summaryRef} className="mt-06 app-card">
        <div className="body">
          {showPlaceholder && (
            <p className="py-06 text-center text-body-md text-text-muted">
              위에서 바꾸고 싶은 요금제나 결제 주기를 선택해 주세요
            </p>
          )}

          {isPreviewRequested && previewQuery.isLoading && <Skeleton height={96} />}

          {/* 결제 주기만 바뀌는 예약(같은 티어, 방향 무관 — 2026-09-10부터 월간→연간도 예약) */}
          {preview && preview.direction === 'CYCLE_CHANGE' && cycleReserveTarget && (
            <div>
              <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center">
                <Badge status="mute">다음 결제일부터 적용</Badge>
                <p className="text-title-lg font-bold text-text">
                  {cycleLabel(cycleReserveTarget === 'annual' ? 'monthly' : 'annual')} → {cycleLabel(cycleReserveTarget)}로 전환
                </p>
              </div>

              <div className="mt-05 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">적용일</span>
                <span className="font-semibold text-text">{formatDate(preview.currentPeriodEnd)}</span>
              </div>
              <div className="mt-03 border-t border-border" />
              <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">그 전까지</span>
                <span className="font-semibold text-text">
                  지금처럼 {cycleLabel(cycleReserveTarget === 'annual' ? 'monthly' : 'annual')} 그대로 이용
                </span>
              </div>
              <div className="mt-05 border-t border-border pt-05">
                <div className="flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                  <span className="text-text-muted">{cycleLabel(cycleReserveTarget)} 구독료</span>
                  <span className="text-text">₩{formatWon(preview.targetMonthlyPrice)}</span>
                </div>
                <div className="mt-02 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                  <span className="text-text-muted">직원 {staffUsageQuery.data?.staffCount ?? 0}명 추가요금(현재 인원 기준)</span>
                  <span className="text-text">
                    {cycleReserveStaffSurcharge > 0 ? '+' : ''}₩{formatWon(cycleReserveStaffSurcharge)}
                  </span>
                </div>
                <div className="mt-03 border-t border-border" />
                <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                  <span className="text-text-muted">{formatDate(preview.currentPeriodEnd)}에 청구 예정</span>
                  <span className="text-title-sm font-bold text-text">
                    ₩{formatWon(preview.targetMonthlyPrice + cycleReserveStaffSurcharge)}
                  </span>
                </div>
              </div>

              <Alert kind="info" className="mt-05">
                지금 바로 청구되는 금액은 없어요. 예약만 해두고, 마음이 바뀌면 언제든 취소할 수 있어요.
                직원 추가요금은 실제 결제일의 소속 인원 기준으로 다시 계산돼서, 그 사이 인원이
                바뀌면 위 예상 금액과 달라질 수 있어요.
              </Alert>

              <div className="mt-05 flex flex-col items-stretch gap-sm sm:flex-row sm:justify-end">
                <Button
                  variant="secondary"
                  onClick={() => (reserveCycleParam ? navigate('/agents/billing/manage') : setBillingCycleOverride(null))}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  loading={reserveCycleChangeMutation.isPending}
                  disabled={reserveCycleChangeMutation.isPending}
                  onClick={() => confirmThenRun({ type: 'cycle', targetBillingCycle: cycleReserveTarget })}
                >
                  {cycleLabel(cycleReserveTarget)}로 전환 예약
                </Button>
              </div>
            </div>
          )}

          {/* 결제 주기 예약 진입인데 더 이상 예약 대상이 아닌 경우 — 이미 배치가 반영했거나, 그 사이
              다른 경로로 상태가 바뀐 경우. direction이 'SAME'으로 떨어지는 건 이 케이스뿐이다(다른
              티어를 골랐다면 SAME이 나올 수 없다) — 카드가 텅 비는 막다른 화면을 막는다(2026-09-07
              발견·수정). */}
          {preview && preview.direction === 'SAME' && (
            <div>
              <Badge status="info">이미 반영됐어요</Badge>
              <p className="mt-03 text-body-sm text-text">
                이미 반영됐거나 예약할 수 있는 상태가 아니에요. 구독·결제 관리에서 현재 상태를
                확인해주세요.
              </p>
              <div className="mt-05 flex flex-col items-stretch gap-sm sm:flex-row sm:justify-end">
                <Button variant="primary" onClick={() => navigate('/agents/billing/manage')}>
                  구독·결제 관리로 이동
                </Button>
              </div>
            </div>
          )}

          {/* 티어+주기 동시 변경(TIER_AND_CYCLE_CHANGE) — 방향(업/다운그레이드 · 월간↔연간) 무관
              항상 예약이다(2026-09-10 정책 변경). reserveCombinedChange 한 번의 호출로 끝난다. */}
          {bothChanged && preview && preview.direction === 'TIER_AND_CYCLE_CHANGE' && !isBlocked && billingCycleOverride && (
            <div>
              <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center">
                <Badge status="mute">다음 결제일부터 적용</Badge>
                <p className="text-title-sm font-bold text-text">
                  {TIER_COPY[preview.currentTierCode].displayName} → {TIER_COPY[preview.targetTierCode].displayName}
                  (으)로 변경 + {cycleLabel(billingCycleOverride)}로 전환
                </p>
              </div>

              <div className="mt-05 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">적용일</span>
                <span className="font-semibold text-text">{formatDate(preview.currentPeriodEnd)}</span>
              </div>
              <div className="mt-03 border-t border-border" />
              <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">그 전까지</span>
                <span className="font-semibold text-text">
                  지금처럼 {TIER_COPY[preview.currentTierCode].displayName} · {cycleLabel(sub!.billingCycle as BillingCycle)} 그대로 이용
                </span>
              </div>
              <div className="mt-05 border-t border-border pt-05">
                <div className="flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                  <span className="text-text-muted">
                    {TIER_COPY[preview.targetTierCode].displayName} · {cycleLabel(billingCycleOverride)} 구독료
                  </span>
                  <span className="text-text">₩{formatWon(preview.targetMonthlyPrice)}</span>
                </div>
                <div className="mt-02 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                  <span className="text-text-muted">직원 {staffUsageQuery.data?.staffCount ?? 0}명 추가요금(현재 인원 기준)</span>
                  <span className="text-text">
                    {combinedReserveStaffSurcharge > 0 ? '+' : ''}₩{formatWon(combinedReserveStaffSurcharge)}
                  </span>
                </div>
                <div className="mt-03 border-t border-border" />
                <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                  <span className="text-text-muted">{formatDate(preview.currentPeriodEnd)}에 청구 예정</span>
                  <span className="text-title-sm font-bold text-text">
                    ₩{formatWon(preview.targetMonthlyPrice + combinedReserveStaffSurcharge)}
                  </span>
                </div>
              </div>

              <Alert kind="info" className="mt-05">
                지금 바로 청구되는 금액은 없어요. 예약만 해두고, 다음 결제일부터 요금제와 결제 주기가
                함께 바뀌어요. 마음이 바뀌면 언제든 취소할 수 있어요. 직원 추가요금은 실제 결제일의
                소속 인원 기준으로 다시 계산돼서, 그 사이 인원이 바뀌면 위 예상 금액과 달라질 수 있어요.
              </Alert>

              <div className="mt-05 flex flex-col items-stretch gap-sm sm:flex-row sm:justify-end">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSelectedTier(null);
                    setBillingCycleOverride(null);
                  }}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  loading={reserveCombinedChangeMutation.isPending}
                  disabled={reserveCombinedChangeMutation.isPending}
                  onClick={() =>
                    confirmThenRun({
                      type: 'combined',
                      targetTierCode: preview.targetTierCode,
                      targetBillingCycle: billingCycleOverride,
                    })
                  }
                >
                  {TIER_COPY[preview.targetTierCode].displayName} · {cycleLabel(billingCycleOverride)}로 함께 예약
                </Button>
              </div>
            </div>
          )}

          {preview && preview.direction === 'UPGRADE' && (
            <div>
              <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center">
                <Badge status="info">즉시 적용</Badge>
                <p className="text-title-sm font-bold text-text">
                  {TIER_COPY[preview.currentTierCode].displayName} → {TIER_COPY[preview.targetTierCode].displayName}
                  (으)로 업그레이드
                </p>
              </div>

              <div className="mt-05 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">새 요금제 월 구독료</span>
                <span className="text-title-sm font-bold text-text">₩{formatWon(preview.targetMonthlyPrice)}</span>
              </div>
              <div className="mt-03 border-t border-border" />
              {/* 직원 추가요금이 있을 때만 내역을 나눠 보여준다(§24) — 없으면 기존처럼 합계 한 줄만 표시 */}
              {preview.staffSurchargeAmt > 0 && (
                <>
                  <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                    <span className="text-text-muted">
                      {preview.remainingDays != null
                        ? `이번 결제 주기 남은 기간(${preview.remainingDays}일)만큼 티어 변경 차액`
                        : '티어 변경 차액(첫 달 요금 전액)'}
                    </span>
                    <span className="text-text">₩{formatWon(preview.proratedDiff - preview.staffSurchargeAmt)}</span>
                  </div>
                  <div className="mt-02 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                    <span className="text-text-muted">직원 추가요금(현재 인원 기준)</span>
                    <span className="text-text">₩{formatWon(preview.staffSurchargeAmt)}</span>
                  </div>
                  <div className="mt-03 border-t border-border" />
                </>
              )}
              <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">
                  {preview.staffSurchargeAmt > 0
                    ? '오늘 결제할 금액'
                    : preview.remainingDays != null
                      ? `이번 결제 주기 남은 기간(${preview.remainingDays}일)만큼 차액`
                      : '지금 결제할 금액(첫 달 요금 전액)'}
                </span>
                <span className="text-title-sm font-bold text-text">₩{formatWon(preview.proratedDiff)}</span>
              </div>

              <Alert kind="info" className="mt-05">
                지금 결제하면 즉시 {TIER_COPY[preview.targetTierCode].displayName}(으)로 전환되고, 다음 결제일(
                {formatDate(preview.currentPeriodEnd)})부터는{' '}
                {preview.staffSurchargeAmt > 0 ? (
                  <>
                    정상 요금 ₩{formatWon(preview.targetMonthlyPrice)}에 직원 추가요금(현재 인원 기준) ₩
                    {formatWon(preview.staffSurchargeAmt)}이 더해져 총 ₩
                    {formatWon(preview.targetMonthlyPrice + preview.staffSurchargeAmt)}이 청구돼요
                  </>
                ) : (
                  <>정상 요금 ₩{formatWon(preview.targetMonthlyPrice)}이 청구돼요</>
                )}
                .
              </Alert>

              <div className="mt-05 flex flex-col items-stretch gap-sm sm:flex-row sm:justify-end">
                <Button variant="secondary" onClick={() => setSelectedTier(null)}>취소</Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    confirmThenRun({ type: 'upgrade', targetTierCode: preview.targetTierCode, diff: preview.proratedDiff })
                  }
                >
                  지금 업그레이드
                </Button>
              </div>
            </div>
          )}

          {preview && preview.direction === 'DOWNGRADE' && !isBlocked && (
            <div>
              <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center">
                <Badge status="mute">
                  {sub?.subStatus === 'TRIAL' ? '체험 전환 시점부터 적용' : '다음 결제일부터 적용'}
                </Badge>
                <p className="text-title-lg font-bold text-text">
                  {TIER_COPY[preview.currentTierCode].displayName} → {TIER_COPY[preview.targetTierCode].displayName}
                  (으)로 다운그레이드
                </p>
              </div>

              <div className="mt-05 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">적용일</span>
                <span className="font-semibold text-text">
                  {/* 체험 중엔 BE 미리보기의 currentPeriodEnd가 CURRENT_PERIOD_END 없음을 감안한 대략적인
                      추정치라 실제 적용 시점(체험 종료일)과 다를 수 있다 — 정확한 trialEndDtm을 대신
                      보여준다. */}
                  {formatDate(sub?.subStatus === 'TRIAL' ? sub.trialEndDtm : preview.currentPeriodEnd)}
                </span>
              </div>
              <div className="mt-03 border-t border-border" />
              <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
                <span className="text-text-muted">그 전까지</span>
                <span className="font-semibold text-text">
                  지금처럼 {TIER_COPY[preview.currentTierCode].displayName} 그대로 이용
                </span>
              </div>

              <Alert kind="info" className="mt-05">
                {sub?.subStatus === 'TRIAL'
                  ? '체험 중엔 청구되지 않아요. 체험이 끝나고 첫 결제가 이뤄질 때 이 요금제 기준으로 청구돼요. 예약은 마음이 바뀌면 언제든 취소할 수 있어요.'
                  : '지금 바로 청구되는 금액은 없어요. 예약만 해두고, 마음이 바뀌면 언제든 취소할 수 있어요.'}
              </Alert>

              <div className="mt-05 flex flex-col items-stretch gap-sm sm:flex-row sm:justify-end">
                <Button variant="secondary" onClick={() => setSelectedTier(null)}>취소</Button>
                <Button
                  variant="primary"
                  loading={reserveDowngradeMutation.isPending}
                  disabled={reserveDowngradeMutation.isPending}
                  onClick={() => confirmThenRun({ type: 'downgrade', targetTierCode: preview.targetTierCode })}
                >
                  {TIER_COPY[preview.targetTierCode].displayName}(으)로 다운그레이드 예약
                </Button>
              </div>
            </div>
          )}

          {preview && (preview.direction === 'DOWNGRADE' || preview.direction === 'TIER_AND_CYCLE_CHANGE') && isBlocked && (
            <div>
              <Badge status="warning">지금은 변경 불가</Badge>
              <p className="mt-03 text-body-sm text-text">
                현재 진행 중 현장이 {activeSiteCount}개인데, {TIER_COPY[preview.targetTierCode].displayName} 요금제는{' '}
                {preview.targetMaxConcurrentSiteLabel}까지만 가능해요. 현장을 정리한 뒤 다시 시도해주세요.
              </p>
              <div className="mt-05 flex flex-col items-stretch gap-sm sm:flex-row sm:justify-end">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSelectedTier(null);
                    setBillingCycleOverride(null);
                  }}
                >
                  취소
                </Button>
                <Button variant="primary" disabled>
                  현장을 먼저 정리해주세요
                </Button>
              </div>
              {/* 현장 관리 화면이 billing 스코프 밖이라 실제 이동 대상 미확정 — 추후작업_TODO.md 참고 */}
              <Button variant="ghost" size="sm" className="mt-02" disabled>
                현장 관리로 이동 →
              </Button>
            </div>
          )}

          {isPreviewRequested && previewQuery.isError && (
            <Alert kind="warning" title="변경 정보를 불러오지 못했습니다">
              잠시 후 다시 선택해주세요.
            </Alert>
          )}
        </div>
      </div>

      {/* 이미 예약된 변경 건이 있는 상태에서 또 다른 변경을 시도할 때 확인(2026-09-18, 사용자 결정) —
          실제 취소·덮어쓰기는 BE가 처리하므로 여기서는 확인만 받고 원래 하려던 요청을 그대로 보낸다. */}
      <Modal
        open={pendingConfirmAction != null}
        size="sm"
        onClose={() => setPendingConfirmAction(null)}
        footerAlign="stretch"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingConfirmAction(null)}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const action = pendingConfirmAction;
                setPendingConfirmAction(null);
                if (action) runTierChangeAction(action);
              }}
            >
              변경하기
            </Button>
          </>
        }
      >
        <p className="text-body-md font-bold text-text">이미 예약된 요금제 변경이 있어요</p>
        <p className="mt-02 text-body-sm text-text-muted">기존 예약을 취소하고 이 내용으로 변경할까요?</p>
      </Modal>
    </Page>
  );
}
